/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Selectors for the location module
 */

import { isBoundsOutsideDelta } from '@zcode-ui/map'
import { createSelector } from '@zcode/memoizers'
import { ZOOM_CLUSTER_FILTER_NOT_APPLICABLE, getQueryBounds } from '@zcode/nr-utils'
import { isBoxSearch } from '@zcode/nr/es/shared/utils/location'
import type { RouteChildrenProps, RouteComponentProps } from '@zcode/router-utils'
import type { State } from '@zcode/state-type'
import { isNumber } from '@zcode/utils'
import { compose } from 'redux'
import {
  selectCurrentCity,
  selectCurrentHood,
} from '../../../../../modules/currentSearch/selectors'
import { selectEntity } from '../../../../detail/modules/entity/selectors'
import {
  selectBreadcrumbFromCurrentSearch,
  selectBreadcrumbFromCurrentSearchMemo,
} from './selectBreadcrumbFromCurrentSearch'

export type { Breadcrumb } from './selectBreadcrumbFromCurrentSearch'

// TODO: move this to the currentSeach module when it's created
export const selectCurrentSearch = (state: State) => state.currentSearch

export const selectLocation = compose((state) => state?.location, selectCurrentSearch)

export const selectBox = compose((state) => state?.box, selectLocation)

export const selectLat = compose((state) => state?.lat, selectLocation)

export const selectLng = compose((state) => state?.lng, selectLocation)

export const selectCenter = createSelector(selectLat, selectLng, (lat, lng) => ({ lat, lng }))

export const selectCityName = compose((state) => state?.cityName, selectLocation)

export const selectHoodName = compose((state) => state?.hoodName, selectLocation)

export const selectStateName = compose((state) => state?.stateName, selectLocation)

export const selectCountry = compose((state) => state?.country, selectLocation)

export const selectUrl = compose((state) => state?.url, selectLocation)

export const selectZoom = compose((state) => state?.zoom, selectLocation)

export const selectMissingLocation = compose((state) => state?.missingLocation, selectLocation)

export const selectLoading = compose((state) => state?.loading, selectLocation)

// NOTE: a location is "ready" when it has a center and zoom; those are the values the map needs
export const selectReady = createSelector(
  selectLat,
  selectLng,
  selectZoom,
  (lat, lng, zoom) => !!(isNumber(lat) && isNumber(lng) && isNumber(zoom))
)

// these selectors should come from the perm-address package, but using them breaks the application
// application does not compile, no error message
const selectPA = (state: State) => state?.permanentAddress
const selectPAHood = compose((pa) => pa?.neighborhood, selectPA)
const selectPACity = compose((pa) => pa?.city, selectPA)
const selectPAActiveListings = compose((pa) => pa?.activeListings, selectPA)
const selectPAInactiveListings = compose((pa) => pa?.inactiveListings, selectPA)
const selectPAAddress = compose((pa) => pa?.address, selectPA)
const selectPAAddressHood = compose((address) => address?.neighborhood, selectPAAddress)

export const selectCityVerticals = (state: State) => {
  return (
    state?.currentSearch?.city?.data?.verticals ?? state?.permanentAddress?.city?.data?.verticals
  )
}
export const selectHoodVerticals = (state: State) => {
  return (
    state?.currentSearch?.hood?.data?.verticals ??
    state?.permanentAddress?.neighborhood?.data?.verticals
  )
}

export const selectBreadcrumb = (
  state: State,
  routeProps?: RouteComponentProps | RouteChildrenProps
) => {
  const cityVerticals = selectCityVerticals(state)
  const hoodVerticals = selectHoodVerticals(state)

  const city = selectCurrentCity(state) ?? selectPACity(state)
  const hood = selectCurrentHood(state) ?? selectPAHood(state)
  const permanentAddressEntity = selectPAActiveListings(state)?.length
    ? selectPAActiveListings(state)?.[0]
    : selectPAInactiveListings(state)?.[0]
  const entity = selectEntity(state) ?? permanentAddressEntity
  const isPermanentAddress = !!selectPAAddress(state)
  const location = isPermanentAddress
    ? {
        box: selectLocation(state)?.box,
        lat: selectPAAddress(state)?.lat ?? 0,
        lng: state?.permanentAddress?.address?.lng ?? 0,
        cityName: selectPAAddressHood(state)?.city ?? permanentAddressEntity?.city ?? null,
        hoodName:
          selectPAAddressHood(state)?.name ?? permanentAddressEntity?.neighborhoodName ?? null,
        stateName: selectPAAddressHood(state)?.state ?? permanentAddressEntity?.state ?? null,
        url: selectPAAddress(state)?.url ?? '',
        loading: selectLocation(state)?.loading,
        missingLocation: selectLocation(state)?.missingLocation,
        zoom: selectLocation(state)?.zoom,
      }
    : selectLocation(state)

  // NOTE: returning unmemoized value if we have an entity
  // since selectBreadcrumbFromCurrentSearch is memoized on location, routeprops.
  // Seems there's a race condition between the time that routeprops changes to trigger the selector,
  // and the time when entity is cleared from state.
  if (entity) {
    return selectBreadcrumbFromCurrentSearch(
      {
        state: location,
        currentCity: city,
        currentHood: hood,
        entity,
        cityVerticals,
        hoodVerticals,
      },
      routeProps
    )
  }
  return selectBreadcrumbFromCurrentSearchMemo(
    {
      state: location,
      currentCity: city,
      currentHood: hood,
      cityVerticals,
      hoodVerticals,
    },
    routeProps
  )
}

export const selectTooFarOut = (state: State) => {
  const location = selectLocation(state)
  if (!location) {
    return undefined
  }
  const { zoom } = location
  if ((zoom ?? 0) <= ZOOM_CLUSTER_FILTER_NOT_APPLICABLE) {
    return true
  }
  if (!zoom && isBoxSearch(location)) {
    const { box } = location
    const bounds = getQueryBounds(box)
    return isBoundsOutsideDelta(bounds)
  }
  return false
}
